import PageBreadcrumb, {
  PageBreadcrumbItem
} from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import GTable from 'components/tables/GTable';
import { jobTypesColumns } from 'components/tables/JobType';
import { ChangeEvent, useMemo, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { GET_JOBTYPE_QUERY } from 'services/api/jobTypes';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSIONS } from 'constants/permissions';
import JobTypeModal from 'components/modals/JobTypeModal';
import LoadingPlaceholder from 'components/loading-placeholder/LoadingPlaceholder';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const defaultBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'Home',
    url: '#!'
  },
  {
    label: 'List Job Type',
    url: '#!'
  },
  {
    label: 'Create Job Type',
    active: true
  }
];

const CreateJobType = (): React.JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);

  const {
    isLoading,
    isError,
    data: jobTypes
  } = useQuery('getJobTypes', GET_JOBTYPE_QUERY, {
    staleTime: 3 * 60 * 60 * 1000
  });

  const [canCreateJobType, canEditJobType, canDeleteJobType] = usePermissions([
    PERMISSIONS.JOB_CONFIGURATION.JOB_TYPE.CREATE,
    PERMISSIONS.JOB_CONFIGURATION.JOB_TYPE.UPDATE,
    PERMISSIONS.JOB_CONFIGURATION.JOB_TYPE.DELETE
  ]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const sortedData = jobTypes?.data?.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const filteredData =
    sortedData
      ?.filter(jobType =>
        jobType.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a: any, b: any) => {
        return a?.name?.localeCompare(b?.name ?? '');
      }) ?? [];

  const columns = useMemo(
    () => jobTypesColumns(canEditJobType, canDeleteJobType),
    [canEditJobType, canDeleteJobType]
  );
  return (
    <>
      <div>
        <PageBreadcrumb items={defaultBreadcrumbItems} />
      </div>
      <div>
        <div className="mb-6">
          {isError && (
            <div className="alert alert-danger" role="alert">
              {isError || 'Error loading job types'}
            </div>
          )}
          <h2 className="mb-3">Job Types</h2>
          <Row className="g-3 align-items-center mb-3">
            <Col>
              <SearchBox
                placeholder="Search by job type name"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
            </Col>
            {canCreateJobType && (
              <Col xs="auto">
                <Button variant="primary" onClick={() => setShowModal(true)}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  New Job Type
                </Button>
              </Col>
            )}
          </Row>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
            {isLoading ? (
              <LoadingPlaceholder />
            ) : (
              <GTable
                data={filteredData || []}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <JobTypeModal
          show={showModal}
          handleClose={handleCloseModal}
          isEditing={false}
        />
      )}
    </>
  );
};

export default CreateJobType;
